import { useEffect, useEffectEvent, useRef } from 'react';

type ViewParams = Record<string, unknown>;

export type TrackedView<P extends ViewParams> = {
  id: string;
  params: P;
  previousParams: P | undefined;
};

export type ViewEngagement = {
  /** Time the view was visible, since the view started or the page became visible again. */
  timeMs: number;
  reason: 'hidden' | 'navigation';
};

/**
 * Tracks views of anything navigable (routes, flow steps) and the time they were visible.
 *
 * - `view.id` is reactive, a new id ends the current view and starts a new one.
 *   Ids are owned by the caller so they can be shared (e.g. as global analytics params)
 *   before this hook's effect runs. `null` means there is no active view (e.g. while loading).
 * - `view.params` are snapshotted when the view starts, so engagement always reports
 *   the params of its own view.
 * - Callbacks are non-reactive, they can read the latest values (e.g. `document.title`).
 *
 * Engagement is reported every time the page gets hidden and when the view ends,
 * total time of a view is the sum of its engagements grouped by `view.id`.
 */
export const useViewTracking = <P extends ViewParams>(options: {
  view: { id: string; params: P } | null;
  onView: (view: TrackedView<P>) => void;
  onEngagement: (view: TrackedView<P>, engagement: ViewEngagement) => void;
}) => {
  const viewId = options.view?.id;
  const previousParams = useRef<P | undefined>(undefined);

  const getView = useEffectEvent(() => options.view);
  const onView = useEffectEvent(options.onView);
  const onEngagement = useEffectEvent(options.onEngagement);

  useEffect(() => {
    const current = getView();
    if (!current) return;

    const view: TrackedView<P> = { ...current, previousParams: previousParams.current };
    onView(view);
    previousParams.current = view.params;

    let visibleSince = document.visibilityState === 'visible' ? performance.now() : undefined;

    const sendEngagement = (reason: ViewEngagement['reason']) => {
      if (visibleSince === undefined) return;
      onEngagement(view, { timeMs: Math.round(performance.now() - visibleSince), reason });
      visibleSince = undefined;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendEngagement('hidden');
      else visibleSince = performance.now();
    };

    // Capture on window runs before the event queue's document listener flushes on hide
    window.addEventListener('visibilitychange', onVisibilityChange, { capture: true });
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange, { capture: true });
      sendEngagement('navigation');
    };
  }, [viewId]);
};
